<template>
    <BaseSheet
        :is-visible="isReservationSheetOpen"
        @close="modalStore.close(MODAL_RESERVATION)"
    >
        <template #title>
            <template v-if="loggedIn && reservationType === RESERVATION_TYPES.WAPRES && hasReservation">
                Mijn reservering
            </template>

            <template v-else>
                Tickets
            </template>
        </template>

        <template #subTitle>
            <EventMeta
                class="event-meta--sheet"
                :event="event"
                :show-price="true"
                :has-reservation="hasReservation"
            />
        </template>

        <template #default>
            <EventFairUsePolicyPanel
                v-if="showFairUsePolicy"
                @close="showFairUsePolicy = false"
            />

            <EventWaitingListInfoPanel
                v-else-if="showWaitingListInfo"
                @close="showWaitingListInfo = false"
            />

            <EventReservationPanelBecomeMember
                v-else-if="!loggedIn"
                :event
            />

            <EventReservationPanelWapres
                v-else-if="reservationType === RESERVATION_TYPES.WAPRES"
                :event="event"
                @close="modalStore.close(MODAL_RESERVATION)"
            />

            <EventReservationPanelParres
                v-else-if="reservationType === RESERVATION_TYPES.PARRES"
                :event="event"
            />

            <EventReservationPanelNores
                v-else-if="reservationType === RESERVATION_TYPES.NORES"
                :event="event"
            />
        </template>

        <template
            v-if="!can('make-reservations') && ticketPartnerUrlGuest"
            #secondary
        >
            <EventReservationPanelGuestTicket :event />
        </template>

        <template
            v-if="loggedIn && !showWaitingListInfo && !showFairUsePolicy"
            #footer
        >
            <p
                v-if="
                    can('make-reservations')
                        && event.isFullyBooked
                        && event.reservationType === RESERVATION_TYPES.WAPRES
                        && !hasReservation
                        && event.waitingListEnabled
                        && (isInWaitingList || userStore.canMakeReservation)"
            >
                <a
                    class="event-reservation-sheet__info-link"
                    style="text-decoration: underline;"
                    @click.prevent="showWaitingListInfo = true"
                >
                    <span>
                        Hoe werkt de wachtlijst?
                    </span>

                    <BaseIcon icon="info" />
                </a>
            </p>

            <template
                v-else-if="
                    can('make-reservations')
                        && (
                            !event.isFullyBooked
                            || event.reservationType !== RESERVATION_TYPES.WAPRES
                            || (hasReservation && paidPrice)
                        )
                        && ticketPricing.needsPayment
                        && ticketPricing.parsedPrice
                        && (
                            paidPrice || !hasReservation
                        )"
            >
                <p v-show="!event.partnerCollectsAdditionalFee">
                    Je hoeft nu nog niet te betalen.
                    Als je bent geweest incasseren we het bedrag aan het begin van de volgende maand
                    via je lopende automatische incasso
                </p>

                <p>
                    <a
                        class="event-reservation-sheet__info-link"
                        style="text-decoration: underline;"
                        @click.prevent="showFairUsePolicy = true"
                    >
                        <span>
                            Waarom een toeslag van €{{ ticketPricing.parsedPrice }}
                        </span>

                        <BaseIcon icon="info" />
                    </a>
                </p>
            </template>
        </template>
    </BaseSheet>
</template>

<script setup lang="ts">
import { MODAL_RESERVATION, useModalStore } from '~/stores/modal';
import { useUserStore } from '~/stores/user';
import * as RESERVATION_TYPES from '~/constants/reservationTypes.js';
import { useEventsStore } from '~/stores/events';

const props = defineProps<{
    event: EventDate;
}>();

const modalStore = useModalStore();
const userStore = useUserStore();
const eventsStore = useEventsStore();

const { loggedIn } = storeToRefs(userStore);
const { can } = userStore;

const showFairUsePolicy = ref(false);
const showWaitingListInfo = ref(false);

const ticketPartnerUrlGuest = computed(() => props.event.ticketPartnerUrlGuest);

const reservationType = computed(() => props.event.reservationType);

const hasReservation = computed(() =>
    !props.event
        ? false
        : userStore.hasReservation(props.event.id));

const ticketPricing = computed(() => eventsStore.ticketPricing);

const paidPrice = computed(() => eventsStore.paidPrice);

const isReservationSheetOpen = computed(() =>
    modalStore.activeModals?.[MODAL_RESERVATION]);

const isInWaitingList = computed(() =>
    props.event
        ? userStore.isInWaitingList(props.event.id)
        : false);
</script>

<style lang="less">
.event-reservation-sheet__info-link {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 2.5rem;
    padding: 0 .875rem;

    cursor: pointer;

    line-height: var(--lineHeight-lg);

    .dn-icon {
        display: block;
        margin: 0;
        margin-left: var(--spacing-2xs, 0.25rem);
        font-size: 1.5rem;
    }

    @media @q-md-min {
        height: 2.625rem;
        padding: 0 1rem;
    }
}
</style>
